import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, FreeMode, Autoplay } from "swiper/modules";
import LightBox from "components/layout/LightBox";
import useAPI from "../../hooks/useAPI";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/free-mode";
import "swiper/css/autoplay";
import "./ProductDetailsSlider.scss";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const ProductDetailsSlider = ({ product }) => {
    const api = useAPI();
    const [imageId, setImageId] = useState();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const mainSwiperRef = useRef(null);

    const images = product?.gallery ?? [];

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 768px)");
        const handleResize = () => setIsMobile(mediaQuery.matches);

        handleResize();

        mediaQuery.addEventListener("change", handleResize);

        return () => {
            mediaQuery.removeEventListener("change", handleResize);
        };
    }, []);

    const updateMainSwiper = (index) => {
        if (mainSwiperRef.current && mainSwiperRef.current.swiper) {
            mainSwiperRef.current.swiper.slideToLoop(index);
        }
    };

    useEffect(() => {
        updateMainSwiper(currentIndex);

        if (thumbsSwiper) {
            thumbsSwiper.slideTo(currentIndex);
        }
    }, [currentIndex, thumbsSwiper]);

    return (
        <>
            <div className="container col-12 product-details-slider-container">
                <div className="row" style={{ height: "100%", position: "relative" }}>
                    {/* Left side thumbnail vertical slider */}

                    {images.length > 1 && !isMobile && (
                        <div className="col-2" style={{ padding: "0px", position: "relative" }}>
                            <div
                                slot="container-start"
                                style={{ cursor: "pointer", display: "flex", justifyContent: "center", position: "relative", top: "-5px", left: 0, right: 0, margin: "0 auto" }}
                                onClick={() => {
                                    thumbsSwiper?.slidePrev();
                                }}
                            >
                                <KeyboardArrowUpIcon />
                            </div>
                            <Swiper
                                onSwiper={(swiper) => setThumbsSwiper(swiper)}
                                direction="vertical"
                                slidesPerView="auto"
                                spaceBetween={10}
                                freeMode={true}
                                watchSlidesProgress={true}
                                modules={[FreeMode, Navigation, Thumbs]}
                                className="thumbnail-slider"
                                style={{ height: "auto", maxHeight: "500px" }}
                                observeParents={true}
                                observer={true}
                                initialSlide={currentIndex}
                            >
                                {images.map(({ image_data }, index) => (
                                    <SwiperSlide key={index} style={{ marginBottom: "10px" }}>
                                        <div className={`thumbnail-slide ${index === currentIndex ? "active" : ""}`}>
                                            <img
                                                src={image_data.url}
                                                alt={`Thumbnail ${index}`}
                                                style={{
                                                    height: "100%",
                                                    width: "100%",
                                                    objectFit: "cover",
                                                    border: index === currentIndex ? "2px solid #007bff" : "none",
                                                }}
                                            />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>

                            <div
                                style={{ cursor: "pointer", display: "flex", justifyContent: "center", position: "relative", bottom: "-5px", left: 0, right: 0, margin: "0 auto" }}
                                onClick={() => {
                                    thumbsSwiper?.slideNext();
                                }}
                            >
                                <KeyboardArrowDownIcon />
                            </div>
                        </div>
                    )}

                    {/* Center horizontal slider */}
                    <div className={images.length > 1 && !isMobile ? "col-10" : "col-12"} style={{ paddingRight: 0, display: "flex", height: "100%" }}>
                        {images.length > 0 ? (
                            <Swiper
                                ref={mainSwiperRef}
                                thumbs={{ swiper: thumbsSwiper }}
                                modules={[Thumbs, Autoplay]}
                                loop={true}
                                autoplay={false}
                                className="main-slider"
                                onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
                                initialSlide={currentIndex}
                            >
                                {images.map(({ image_data }, index) => (
                                    <SwiperSlide key={index}>
                                        <div className="main-slide">
                                            <img
                                                style={{
                                                    objectFit: "contain",
                                                    width: "100%",
                                                    cursor: "pointer",
                                                }}
                                                alt={`Product ${index}`}
                                                src={image_data.url}
                                                onClick={() => setImageId(index)}
                                            />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        ) : (
                            <img
                                src={api.buildPath("/configuration/images/presentation/basic/missing")}
                                style={{
                                    maxHeight: 450,
                                    width: "100%",
                                    objectFit: "cover",
                                    height: "100%",
                                }}
                                alt="Product not available"
                            />
                        )}
                    </div>
                </div>
            </div>

            {imageId !== undefined && <LightBox openId={imageId} gallery={images} setImageId={setImageId} setCurrentIndex={setCurrentIndex} currentIndex={currentIndex} />}
        </>
    );
};

export default ProductDetailsSlider;
